import { useEffect, useState, Component } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { Header } from "@/components/site/Header";
import { Hero } from "@/components/site/Hero";
import { Journeys } from "@/components/site/Journeys";
import { About } from "@/components/site/About";
import { Banks } from "@/components/site/Banks";
import { Authority } from "@/components/site/Authority";
import { Services } from "@/components/site/Services";
import { Process } from "@/components/site/Process";
import { Integrity } from "@/components/site/Integrity";
import { Region } from "@/components/site/Region";
import { LeadSection } from "@/components/site/LeadSection";
import { FaqHome } from "@/components/site/FaqHome";
import { Contact } from "@/components/site/Contact";
import { Footer } from "@/components/site/Footer";
import { WhatsAppFab } from "@/components/site/WhatsAppFab";
import { MobileActionBar } from "@/components/site/MobileActionBar";
import { CookieNotice } from "@/components/site/CookieNotice";
import { ServicePage } from "@/components/site/ServicePage";
import { ProfilePage } from "@/components/site/ProfilePage";
import { PrivacyPage } from "@/components/site/PrivacyPage";
import { MunicipalityPage } from "@/components/site/MunicipalityPage";
import { SERVICE_SLUGS } from "@/lib/servicesData";
import { PROFILE_SLUGS } from "@/lib/profilesData";
import { trackWhatsApp, trackTelefone, trackEmail, trackLinkedIn, consentGranted, grantAnalytics } from "@/lib/analytics";

class ErrorBoundary extends Component {
  state = { error: null };
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="grid min-h-[60vh] place-items-center bg-[#F9F8F3] px-6 text-center">
          <div>
            <p className="font-serif text-3xl text-[#1A2218]">Algo saiu do trilho.</p>
            <p className="mt-3 text-[#556052]">Recarregue a página para voltar ao site.</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const Home = () => (
  <>
    <Hero />
    <Journeys />
    <About />
    <Banks />
    <Authority />
    <Services />
    <Process />
    <Integrity />
    <Region />
    <LeadSection />
    <FaqHome />
    <Contact />
  </>
);

const NotFound = () => (
  <main className="grid min-h-[70svh] place-items-center bg-[#F9F8F3] px-6 pt-[72px] text-center">
    <div data-testid="not-found">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#5D995E]">Página não encontrada</p>
      <h1 className="mt-4 font-serif text-5xl font-semibold text-[#1A2218]">Essa trilha não existe por aqui.</h1>
      <a href="/" className="mt-8 inline-flex rounded-full bg-[#233D25] px-7 py-4 font-semibold text-[#F9F8F3]">Voltar ao início</a>
    </div>
  </main>
);

const ScrollManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

function App() {
  const [cookieBar, setCookieBar] = useState(true);
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2, smoothWheel: true });
    window.__lenis = lenis;
    let id;
    const raf = (t) => {
      lenis.raf(t);
      id = requestAnimationFrame(raf);
    };
    id = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(id);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  useEffect(() => {
    // Medições globais: WhatsApp, telefone, e-mail e LinkedIn a partir de qualquer página
    const onClick = (e) => {
      const a = e.target && e.target.closest ? e.target.closest("a") : null;
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (href.includes("wa.me")) trackWhatsApp(window.location.pathname);
      else if (href.startsWith("tel:")) trackTelefone(window.location.pathname);
      else if (href.startsWith("mailto:")) trackEmail(window.location.pathname);
      else if (href.includes("linkedin.com")) trackLinkedIn(window.location.pathname);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  useEffect(() => {
    // Retornando de uma visita anterior que aceitou os cookies: concede e inicia a medição.
    if (consentGranted()) grantAnalytics();
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <MotionConfig reducedMotion="user">
          <ScrollManager />
          <Header />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/politica-de-privacidade" element={<PrivacyPage />} />
            <Route path="/municipios/pelotas" element={<MunicipalityPage />} />
            {SERVICE_SLUGS.map((slug) => (
              <Route key={slug} path={`/${slug}`} element={<ServicePage slug={slug} />} />
            ))}
            {PROFILE_SLUGS.map((slug) => (
              <Route key={slug} path={`/${slug}`} element={<ProfilePage slug={slug} />} />
            ))}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Footer />
          <WhatsAppFab lifted={cookieBar} />
          <MobileActionBar />
          <CookieNotice onVisibilityChange={setCookieBar} />
          <Toaster position="bottom-center" richColors />
        </MotionConfig>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
